/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import React, { useEffect } from 'react';
import PropertySet from '../PropertySet';
import SpellPopup from '../SpellPopup';
import Spell from '../model/Spell';

interface SpellLevelSelectorProps {
  level: number;
  notes: string;
  maxSpellCount: number;
  spellsKnowableArray: Spell[];
  spellsSelectedArray: Spell[];
  onSpellsSelectedChanged: (level: number, newValue: Spell[]) => void;
  openLevel: number; // -1 means nothing is open
  setOpenLevel: (level: number) => void;
  onClickOutside: (event: MouseEvent) => void;
  filterFunction?: (spell: Spell) => boolean;
  columns?: number;
  showGrouping?: boolean;
}

const SpellLevelSelector: React.FC<SpellLevelSelectorProps> = ({
  level,
  notes,
  maxSpellCount,
  spellsKnowableArray,
  spellsSelectedArray,
  onSpellsSelectedChanged,
  openLevel,
  setOpenLevel,
  onClickOutside,
  filterFunction,
  columns = 3,
  showGrouping = true
}) => {
  useEffect(() => {
    // Add any necessary effect logic here
  }, []); // Dependencies can be added if they affect the spells data

  const handleSpellSelectionChange = (newValue: Spell[]) => {
    onSpellsSelectedChanged(level, newValue);
  };

  const getIdFunction = (spell: Spell): string => { return spell.key};

  const displayFunction = (spell: Spell | null): JSX.Element => {
    return (
      <span className="spell-list">
        {spell && <SpellPopup spell={spell} /> }
      </span>
    );
  };

  return (
    <PropertySet
      className="spell-selector"
      columns={columns}
      showGrouping={showGrouping}
      values={null}
      alwaysShow={true}
      isOpen={openLevel === level}
      setIsOpen={(newIsOpen) => setOpenLevel(newIsOpen ? level : -1)}
      name={notes}
      options={spellsKnowableArray}
      valuesArray={spellsSelectedArray}
      maxSelections={maxSpellCount}
      filterFunction={filterFunction}
      onSaveArray={handleSpellSelectionChange}
      displayFunction={displayFunction}
      getIdFunction={getIdFunction}
      onClickOutside={onClickOutside}
    />
  );
};

export default SpellLevelSelector;
